import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, Edit, Trash2 } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useToast } from '@/hooks/use-toast';

export const ViewChangeOrderDialog = ({ open, onOpenChange, changeOrder, onEdit, onDelete, project, customer }) => {
  const { toast } = useToast();
  
  if (!changeOrder) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Company Header - Left side
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Reliable Landscaping and Design', 20, 20);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('License #1101544', 20, 27);
    doc.text('916-918-3990', 20, 32);
    doc.text('www.ReliableLandscapesDesign.com', 20, 37);
    
    // Document Title - Centered
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('CHANGE ORDER', pageWidth / 2, 50, { align: 'center' });
    
    // Customer Info Section - Right side
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const rightX = 110;
    doc.text(`Customer:`, rightX, 65);
    const customerFullName = `${customer?.name || ''} ${customer?.last_name || ''}`.trim() || 'N/A';
    doc.text(customerFullName, rightX + 30, 65);
    
    doc.text(`Project Address:`, rightX, 72);
    const address = project?.jobsite_address || project?.address || 'N/A';
    const addressLines = doc.splitTextToSize(address, 65);
    doc.text(addressLines, rightX + 30, 72);
    
    doc.text(`Date:`, rightX, 79 + (addressLines.length - 1) * 5);
    doc.text(`${new Date(changeOrder.created_at).toLocaleDateString('en-US')}`, rightX + 30, 79 + (addressLines.length - 1) * 5);
    
    // Scope of Work Section
    const scopeStartY = 95 + (addressLines.length - 1) * 5;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Scope of Work', 20, scopeStartY);
    
    // Line items table
    const tableData = changeOrder.change_order_data.map(item => [
      item.description,
      item.quantity.toString(),
      formatCurrency(item.price),
      formatCurrency(item.price * item.quantity)
    ]);
    
    autoTable(doc, {
      startY: scopeStartY + 5,
      head: [['Description', 'Quantity', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { 
        fillColor: [41, 128, 185],
        fontSize: 11,
        fontStyle: 'bold',
        halign: 'left'
      },
      bodyStyles: {
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' }
      },
      margin: { left: 20, right: 20 }
    });
    
    const finalY = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || 150) + 15;
    
    // Cost Summary Section
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Cost Summary', 20, finalY);
    
    // Cost summary table
    const costData = [
      ['Change Order Total', formatCurrency(changeOrder.total)],
      ['Previous Job Total', formatCurrency(changeOrder.previous_total_job_cost)],
      ['New Project Total', formatCurrency(changeOrder.new_project_total)]
    ];
    
    autoTable(doc, {
      startY: finalY + 5,
      body: costData,
      theme: 'plain',
      bodyStyles: {
        fontSize: 11
      },
      columnStyles: {
        0: { cellWidth: 80, fontStyle: 'bold' },
        1: { cellWidth: 50, halign: 'right', fontStyle: 'bold' }
      },
      margin: { left: 20 }
    });
    
    const costSummaryEndY = (doc.lastAutoTable?.finalY || finalY + 35) + 15;
    
    // Terms & Conditions Section
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions', 20, costSummaryEndY);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    let termsY = costSummaryEndY + 7;
    const terms = [
      'Scope & Authorization: This Change Order modifies the original contract. Work will commence upon signed approval.',
      'Pricing & Payments: All prices are final. Payment terms remain as per the original contract.',
      'Delays & Scheduling: Changes may affect the project timeline. New completion dates will be communicated.'
    ];
    
    terms.forEach(term => {
      const lines = doc.splitTextToSize(term, pageWidth - 40);
      doc.text(lines, 20, termsY);
      termsY += lines.length * 5 + 3;
    });
    
    // Check if we need a new page for signature
    if (termsY > 240) {
      doc.addPage();
      termsY = 20;
    }
    
    // Warranty section
    termsY += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Warranty', 20, termsY);
    doc.setFont('helvetica', 'normal');
    termsY += 7;
    const warrantyText = doc.splitTextToSize(
      'All work performed under this Change Order is warranted for the same period as the original contract.',
      pageWidth - 40
    );
    doc.text(warrantyText, 20, termsY);
    termsY += warrantyText.length * 5 + 8;
    
    // Binding Agreement
    doc.setFont('helvetica', 'bold');
    doc.text('Binding Agreement', 20, termsY);
    doc.setFont('helvetica', 'normal');
    termsY += 7;
    const agreementText = doc.splitTextToSize(
      'By signing below, both parties agree to the terms outlined in this Change Order and acknowledge that it becomes part of the original contract.',
      pageWidth - 40
    );
    doc.text(agreementText, 20, termsY);
    termsY += agreementText.length * 5 + 10;
    
    // Check if we need a new page for signatures
    if (termsY > 220) {
      doc.addPage();
      termsY = 30;
    }
    
    // Customer Signature Section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Signature:', 20, termsY);
    
    // Add signature image if present, otherwise leave blank space
    if (changeOrder.signature) {
      doc.addImage(changeOrder.signature, 'PNG', 20, termsY + 5, 60, 20);
      termsY += 25;
    } else {
      // Leave blank space for signature
      termsY += 25;
    }
    
    // Signature line and date for customer
    doc.setFont('helvetica', 'normal');
    doc.line(20, termsY, 90, termsY);
    doc.setFontSize(9);
    doc.text('Signature', 20, termsY + 5);
    doc.text(`Date: ${new Date(changeOrder.created_at).toLocaleDateString('en-US')}`, 20, termsY + 10);
    
    // Contractor Signature Section
    const contractorY = termsY - 25;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Contractor Signature:', 110, contractorY);
    
    // Blank space for contractor signature
    doc.setFont('helvetica', 'normal');
    doc.line(110, termsY, 180, termsY);
    doc.setFontSize(9);
    doc.text('Signature', 110, termsY + 5);
    doc.text('Date: ________________', 110, termsY + 10);
    
    return doc;
  };

  const handleDownload = () => {
    try {
      const doc = generatePDF();
      const fileName = `Change_Order_${project?.name || 'Project'}_${new Date(changeOrder.created_at).toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast({
        title: 'Success',
        description: 'Change order PDF downloaded successfully.',
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: 'Error',
        description: `Failed to generate PDF: ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Change Order Details</span>
            <Badge variant={changeOrder.status === 'Signed' ? 'default' : 'secondary'}>
              {changeOrder.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">Date Created</label>
              <p className="text-sm">{formatDate(changeOrder.created_at)}</p>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Last Updated</label>
              <p className="text-sm">{formatDate(changeOrder.updated_at)}</p>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Scope of Work</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {changeOrder.change_order_data.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.price)}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(item.price * item.quantity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Cost Summary */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="font-semibold">Change Order Total:</span>
              <span className="font-bold text-blue-600">{formatCurrency(changeOrder.total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Previous Total Job Cost:</span>
              <span>{formatCurrency(changeOrder.previous_total_job_cost)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between">
              <span className="font-bold">New Project Total:</span>
              <span className="font-bold text-green-600">
                {formatCurrency(changeOrder.new_project_total)}
              </span>
            </div>
          </div>

          {/* Signature */}
          {changeOrder.signature && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Customer Signature</h3>
              <div className="border rounded-lg p-4 bg-white">
                <img 
                  src={changeOrder.signature} 
                  alt="Customer Signature" 
                  className="max-h-32 object-contain"
                />
              </div>
            </div>
          )}

          {!changeOrder.signature && changeOrder.status === 'Unsigned' && (
            <div className="text-sm text-gray-500 italic">
              No signature - change order is unsigned
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDownload}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
            {onEdit && (
              <Button
                variant="outline"
                onClick={() => {
                  onEdit(changeOrder);
                  onOpenChange(false);
                }}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                variant="destructive"
                onClick={() => {
                  onDelete(changeOrder);
                  onOpenChange(false);
                }}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
